import { supabase } from '../lib/supabase';

// Helper to check if Supabase is configured
function isSupabaseConfigured() {
  return supabase && supabase.from !== undefined;
}

// Helper to check schema errors
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
}

// ============================================
// JOBS SERVICE
// ============================================

export const jobsService = {
  // Get all active jobs with filters
  async getJobs(filters = {}) {
    try {
      // Check if Supabase is configured
      if (!isSupabaseConfigured()) {
        throw new Error('Database connection not configured');
      }

      let query = supabase?.from('jobs')?.select(`
          *,
          user_profiles!jobs_user_id_fkey(
            id,
            full_name,
            avatar_url,
            email
          )
        `)?.eq('status', 'active');

      if (filters?.category && filters?.category !== 'all') {
        query = query?.eq('category', filters?.category);
      }

      if (filters?.location && filters?.location !== 'all') {
        query = query?.ilike('city', `%${filters?.location}%`);
      }

      if (filters?.budgetMin) {
        query = query?.gte('budget_min', filters?.budgetMin);
      }

      if (filters?.budgetMax) {
        query = query?.lte('budget_max', filters?.budgetMax);
      }

      if (filters?.verifiedOnly) {
        query = query?.in('verification_level', ['verified', 'premium']);
      }

      if (filters?.urgentOnly) {
        query = query?.eq('urgency', 'urgent');
      }

      if (filters?.datePosted && filters?.datePosted !== 'all') {
        const daysAgo = {
          'today': 1,
          'week': 7,
          'month': 30
        }?.[filters?.datePosted];
        
        if (daysAgo) {
          const date = new Date();
          date?.setDate(date?.getDate() - daysAgo);
          query = query?.gte('created_at', date?.toISOString());
        }
      }

      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Jobs fetch error:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get jobs error:', error);
      throw error;
    }
  },

  // Get single job by ID
  async getJobById(jobId) {
    try {
      const { data, error } = await supabase?.from('jobs')?.select(`
          *,
          user_profiles!jobs_user_id_fkey(
            id,
            full_name,
            avatar_url,
            email
          )
        `)?.eq('id', jobId)?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Job fetch error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Get job by ID error:', error);
      throw error;
    }
  },

  // Create new job
  async createJob(jobData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const jobPayload = toSnakeCase({
        ...jobData,
        userId: user?.id,
        status: 'active'
      });

      const { data, error } = await supabase?.from('jobs')?.insert(jobPayload)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Job creation error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Create job error:', error);
      throw error;
    }
  },

  // Update job
  async updateJob(jobId, updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const updatePayload = toSnakeCase(updates);

      const { data, error } = await supabase?.from('jobs')?.update(updatePayload)?.eq('id', jobId)?.eq('user_id', user?.id)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Job update error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Update job error:', error);
      throw error;
    }
  },

  // Get user's jobs
  async getUserJobs(userId) {
    try {
      const { data, error } = await supabase?.from('jobs')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('User jobs fetch error:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get user jobs error:', error);
      throw error;
    }
  }
};

// ============================================
// APPLICATIONS SERVICE
// ============================================

export const applicationsService = {
  // Submit application
  async submitApplication(applicationData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const appPayload = toSnakeCase({
        ...applicationData,
        workerId: user?.id,
        status: 'pending'
      });

      const { data, error } = await supabase?.from('applications')?.insert(appPayload)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Application submission error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Submit application error:', error);
      throw error;
    }
  },

  // Get user's applications
  async getUserApplications(userId) {
    try {
      const { data, error } = await supabase?.from('applications')?.select(`*,jobs!applications_job_id_fkey(id,title,category,budget_min,budget_max,city,user_profiles!jobs_user_id_fkey(full_name,avatar_url))`)?.eq('worker_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('User applications fetch error:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get user applications error:', error);
      throw error;
    }
  },

  // Get applications for a job
  async getJobApplications(jobId) {
    try {
      const { data, error } = await supabase?.from('applications')?.select(`*,user_profiles!applications_worker_id_fkey(id,full_name,avatar_url,email),worker_profiles!applications_worker_id_fkey(rating,total_reviews,total_jobs_completed,skills)`)?.eq('job_id', jobId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Job applications fetch error:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get job applications error:', error);
      throw error;
    }
  },

  // Update application status
  async updateApplicationStatus(applicationId, status) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('applications')?.update({ status })?.eq('id', applicationId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Application status update error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Update application status error:', error);
      throw error;
    }
  }
};

// ============================================
// WORKER PROFILES SERVICE
// ============================================

export const workerProfilesService = {
  // Get worker profile
  async getWorkerProfile(userId) {
    try {
      const { data, error } = await supabase?.from('worker_profiles')?.select(`
          *,
          user_profiles!worker_profiles_user_id_fkey(
            full_name,
            avatar_url,
            email
          )
        `)?.eq('user_id', userId)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Worker profile fetch error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Get worker profile error:', error);
      throw error;
    }
  },

  // Get workers with advanced filters
  async getWorkers(filters = {}) {
    try {
      let query = supabase?.from('worker_profiles')?.select(`
          *,
          user_profiles!worker_profiles_user_id_fkey(
            id,
            full_name,
            avatar_url,
            email
          )
        `);

      // Filter by skill tags
      if (filters?.skills && filters?.skills?.length > 0) {
        query = query?.overlaps('skills', filters?.skills);
      }

      // Filter by availability
      if (filters?.availability && filters?.availability !== 'all') {
        query = query?.eq('availability', filters?.availability);
      }

      // Filter by minimum rating
      if (filters?.minRating) {
        query = query?.gte('rating', filters?.minRating);
      }

      // Filter by verified status
      if (filters?.verifiedOnly) {
        query = query?.eq('is_verified', true);
      }

      // Filter by verification level
      if (filters?.verificationLevel && filters?.verificationLevel !== 'all') {
        query = query?.eq('verification_level', filters?.verificationLevel);
      }

      // Filter by category (primary skill)
      if (filters?.category && filters?.category !== 'all') {
        query = query?.contains('skills', [filters?.category]);
      }

      // Filter by location
      if (filters?.location && filters?.location !== 'all') {
        // Join with user_profiles to filter by location if needed
        // For now, this is a placeholder as location is not in worker_profiles
      }

      // Filter by hourly rate range
      if (filters?.rateMin) {
        query = query?.gte('hourly_rate_min', filters?.rateMin);
      }

      if (filters?.rateMax) {
        query = query?.lte('hourly_rate_max', filters?.rateMax);
      }

      // Order by rating by default
      query = query?.order('rating', { ascending: false });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Workers fetch error:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get workers error:', error);
      throw error;
    }
  },

  // Create or update worker profile
  async upsertWorkerProfile(profileData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const profilePayload = toSnakeCase({
        ...profileData,
        userId: user?.id
      });

      const { data, error } = await supabase?.from('worker_profiles')?.upsert(profilePayload, { onConflict: 'user_id' })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Worker profile upsert error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Upsert worker profile error:', error);
      throw error;
    }
  }
};

// ============================================
// RATINGS SERVICE
// ============================================

export const ratingsService = {
  // Create rating
  async createRating(ratingData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const ratingPayload = toSnakeCase({
        ...ratingData,
        fromUserId: user?.id
      });

      const { data, error } = await supabase?.from('ratings')?.insert(ratingPayload)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Rating creation error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Create rating error:', error);
      throw error;
    }
  },

  // Get ratings for a user
  async getUserRatings(userId) {
    try {
      const { data, error } = await supabase?.from('ratings')?.select(`
          *,
          from_user:user_profiles!ratings_from_user_id_fkey(
            full_name,
            avatar_url
          ),
          jobs!ratings_job_id_fkey(
            title
          )
        `)?.eq('to_user_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('User ratings fetch error:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get user ratings error:', error);
      throw error;
    }
  }
};

// ============================================
// EARNINGS SERVICE
// ============================================

export const earningsService = {
  // Get user earnings
  async getUserEarnings(userId, year = new Date()?.getFullYear()) {
    try {
      const { data, error } = await supabase?.from('earnings')?.select('*')?.eq('user_id', userId)?.eq('period_year', year)?.order('period_month', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('User earnings fetch error:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get user earnings error:', error);
      throw error;
    }
  },

  // Create earning record
  async createEarning(earningData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const earningPayload = toSnakeCase({
        ...earningData,
        userId: user?.id
      });

      const { data, error } = await supabase?.from('earnings')?.insert(earningPayload)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Earning creation error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Create earning error:', error);
      throw error;
    }
  },

  // Get earnings summary
  async getEarningsSummary(userId) {
    try {
      const currentYear = new Date()?.getFullYear();
      const { data, error } = await supabase?.from('earnings')?.select('amount, period_month')?.eq('user_id', userId)?.eq('period_year', currentYear);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Earnings summary fetch error:', error?.message);
        return { total: 0, monthly: [] };
      }

      const total = data?.reduce((sum, item) => sum + parseFloat(item?.amount || 0), 0) || 0;
      const monthly = toCamelCase(data || []);

      return { total, monthly };
    } catch (error) {
      console.error('Get earnings summary error:', error);
      throw error;
    }
  }
};

// ============================================
// USER PROFILES SERVICE
// ============================================

export const userProfilesService = {
  // Get user profile
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('User profile fetch error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Get user profile error:', error);
      throw error;
    }
  },

  // Update user profile
  async updateUserProfile(updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const updatePayload = toSnakeCase(updates);

      const { data, error } = await supabase?.from('user_profiles')?.update(updatePayload)?.eq('id', user?.id)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('User profile update error:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Update user profile error:', error);
      throw error;
    }
  }
};

export { toCamelCase, toSnakeCase };